import React, { useState, useEffect } from "react";
import { FaTimes, FaCamera, FaPaperPlane, FaExclamationTriangle, FaCheckCircle, FaImages } from "react-icons/fa";
import imageCompression from "browser-image-compression";

const ComplaintModal = ({ isOpen, onClose, establishmentId, backEnd_request, establishmentName, verifiedPhone, phoneToken, onTokenInvalid }) => {
    const [formData, setFormData] = useState({
        complaintText: "",
        phoneNumber: verifiedPhone || "",
        phoneToken: phoneToken || ""
    });

    const [selectedPhotos, setSelectedPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [complaintId, setComplaintId] = useState(null);
    const [status, setStatus] = useState({ type: "", msg: "" });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            phoneNumber: verifiedPhone || prev.phoneNumber,
            phoneToken: phoneToken || prev.phoneToken
        }));
    }, [verifiedPhone, phoneToken]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (selectedPhotos.length + files.length > 2) {
            setStatus({ type: "danger", msg: "عذراً، الحد الأقصى هو صورتين فقط للشكوى الواحد." });
            return;
        }
        const validFiles = files.filter(file => file.type.startsWith("image/"));
        setSelectedPhotos(prev => [...prev, ...validFiles]);
        setStatus({ type: "", msg: "" }); // إعادة تعيين التنبيه عند الاختيار الصحيح
    };

    const removePhoto = (index) => {
        setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const resetAndClose = () => {
        setFormData({ complaintText: "", phoneNumber: verifiedPhone, phoneToken: phoneToken });
        setSelectedPhotos([]);
        setStatus({ type: "", msg: "" });
        setIsSuccess(false);
        setComplaintId(null);
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.phoneToken) {
            setStatus({ type: "danger", msg: "جلسة التحقق غير صالحة، يرجى إعادة التحقق من الهاتف." });
            if (onTokenInvalid) setTimeout(() => onTokenInvalid(), 2000);
            return;
        }

        setSubmitting(true);
        setStatus({ type: "info", msg: "جاري معالجة الصور وإرسال الشكوى..." });

        const data = new FormData();
        data.append("EstablishmentFieldUid", establishmentId);
        data.append("ComplaintText", formData.complaintText);
        data.append("PhoneNumber", formData.phoneNumber);
        data.append("PhoneToken", formData.phoneToken);

        try {
            const compressionOptions = {
                maxSizeMB: 0.8,
                maxWidthOrHeight: 1200,
                useWebWorker: true,
                fileType: "image/jpeg"
            };

            for (const file of selectedPhotos) {
                const compressedFile = await imageCompression(file, compressionOptions);
                const finalFile = new File(
                    [compressedFile],
                    file.name.replace(/\.[^/.]+$/, "") + ".jpg",
                    { type: "image/jpeg" }
                );
                data.append("Photos", finalFile);
            }

            const response = await backEnd_request.post("/public/complaints", data, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (response.data && response.data.publicComplaintId) {
                setComplaintId(response.data.publicComplaintId);
            }

            setIsSuccess(true);
        } catch (error) {
            const errorMsg = error.response?.data?.message || "فشل إرسال الشكوى، يرجى المحاولة لاحقاً.";
            setStatus({ type: "danger", msg: errorMsg });
            if (error.response?.status === 401 && onTokenInvalid) {
                setTimeout(() => onTokenInvalid(), 2000);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={resetAndClose}>
            <div className="modal-glass-card shadow-lg animate-fade-in" onClick={e => e.stopPropagation()}>

                {isSuccess ? (
                    <div className="text-center py-4 animate-scale-up" dir="rtl">
                        <div className="success-icon-wrapper mb-3">
                            <FaCheckCircle />
                        </div>
                        <h4 className="fw-bold text-dark mb-2">تم استلام شكواك</h4>

                        <div className="complaint-id-box my-4 p-3 rounded-4 shadow-sm border border-success border-opacity-25">
                            <p className="text-muted small mb-1">رقم الشكوى الخاص بك</p>
                            <div className="d-flex align-items-center justify-content-center gap-2">
                                <span className="fs-2 fw-bold text-success font-monospace">{complaintId}</span>
                            </div>
                            <p className="mb-0 mt-2 small text-dark fw-medium">
                                يرجى الاحتفاظ بهذا الرقم لمتابعة الشكوى لاحقاً
                            </p>
                        </div>

                        <p className="text-muted mb-4 px-3 small">
                            نشكرك على تعاونك. تم إرسال الشكوى وجاري مراجعتها من قبل الفريق المختص.
                        </p>

                        <button onClick={resetAndClose} className="btn btn-success w-100 rounded-pill py-2 fw-bold shadow-sm transition-all">
                            إغلاق النافذة
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="modal-header-custom border-bottom pb-3 mb-3 d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                                <div className="icon-badge-red"><FaExclamationTriangle /></div>
                                <div>
                                    <h5 className="fw-bold mb-0 text-dark">تقديم شكوى رسمية</h5>
                                    <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }}>الفعالية: {establishmentName || "غير محدد"}</small>
                                </div>
                            </div>
                            <button className="btn-close-circle" onClick={resetAndClose}><FaTimes /></button>
                        </div>

                        {status.msg && (
                            <div className={`alert alert-${status.type} py-2 small border-0 shadow-sm mb-3 text-end animate-fade-in`} dir="rtl">
                                {submitting && <span className="spinner-border spinner-border-sm ms-2"></span>}
                                {status.msg}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="text-end" dir="rtl">
                            <div className="mb-2">
                                <label className="form-label small fw-bold text-muted">تفاصيل الشكوى *</label>
                                <textarea
                                    name="complaintText"
                                    className="form-control bg-light border-0 shadow-none"
                                    rows="4"
                                    placeholder="يرجى كتابة تفاصيل الشكوى بوضوح..."
                                    required
                                    value={formData.complaintText}
                                    onChange={handleChange}
                                    maxLength={500}
                                ></textarea>
                            </div>

                            <div className="d-flex justify-content-between mt-1 mb-3 px-1">
                                <small className="text-muted" style={{ fontSize: '10px' }}>الحد الأقصى 500 حرف</small>
                                <small className={`${formData.complaintText.length >= 500 ? 'text-danger fw-bold' : 'text-muted'}`} style={{ fontSize: '11px' }} dir="ltr">
                                    {formData.complaintText.length} / 500
                                </small>
                            </div>

                            <div className="mb-4">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label className="form-label small fw-bold text-muted mb-0">إرفاق إثبات (صور)</label>
                                    <small className="text-muted">{selectedPhotos.length} / 2</small>
                                </div>

                                <div className="d-flex gap-2">
                                    <label className="upload-btn-split flex-grow-1">
                                        <FaCamera className="ms-2" /> تصوير
                                        <input type="file" accept="image/*" capture="environment" hidden onChange={handleFileChange} />
                                    </label>
                                    <label className="upload-btn-split flex-grow-1">
                                        <FaImages className="ms-2" /> المعرض
                                        <input type="file" multiple accept="image/*" hidden onChange={handleFileChange} />
                                    </label>
                                </div>

                                <div className="d-flex flex-wrap gap-2 justify-content-start mt-2">
                                    {selectedPhotos.map((file, index) => (
                                        <div key={index} className="photo-chip animate-fade-in">
                                            <small className="text-truncate" style={{ maxWidth: '100px' }}>{file.name}</small>
                                            <FaTimes className="cursor-pointer ms-1" onClick={() => removePhoto(index)} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label small fw-bold text-muted">رقم الهاتف المستخدم</label>
                                <input
                                    name="phoneNumber"
                                    type="tel"
                                    className="form-control bg-light border-0 shadow-none text-muted text-center fw-bold"
                                    value={formData.phoneNumber}
                                    readOnly
                                />
                                <small className="text-success fw-bold d-block mt-1" style={{ fontSize: '10px' }}>✓ تم التحقق من هويتك</small>
                            </div>

                            <button type="submit" className="btn btn-danger w-100 fw-bold py-2 shadow-sm rounded-pill mt-2" disabled={submitting}>
                                {submitting ? "جاري الإرسال..." : <><FaPaperPlane className="ms-2" /> إرسال الشكوى</>}
                            </button>
                        </form>
                    </>
                )}
            </div>

            <style>{`
                .modal-overlay { position: fixed; top:0; left:0; width:100%; height:100%; background: rgba(15, 23, 42, 0.85); backdrop-filter: blur(8px); display: flex; justify-content: center; align-items: center; z-index: 2000; }
                .modal-glass-card { background: white; width: 92%; max-width: 420px; border-radius: 25px; padding: 25px; position: relative; border: 1px solid rgba(255,255,255,0.2); }
                .icon-badge-red { width: 45px; height: 45px; background: #fee2e2; color: #dc2626; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
                .btn-close-circle { border: none; background: #f1f5f9; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: #64748b; transition: 0.2s; }
                .btn-close-circle:hover { background: #e2e8f0; color: #1e293b; }
                
                .upload-btn-split { cursor: pointer; background: #f8fafc; padding: 10px; border-radius: 12px; border: 1px solid #e2e8f0; font-size: 13px; color: #475569; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
                .upload-btn-split:hover { background: #f1f5f9; border-color: #cbd5e1; }
                
                .photo-chip { background: #eff6ff; color: #2563eb; padding: 5px 10px; border-radius: 10px; font-size: 11px; display: flex; align-items: center; border: 1px solid #dbeafe; }
                .form-control { border-radius: 15px !important; font-size: 14px; padding: 12px; }
                .success-icon-wrapper { font-size: 4rem; color: #22c55e; }
                .complaint-id-box { background-color: #f0fdf4; }
                .btn-success { background-color: #22c55e; border: none; }
                .transition-all { transition: all 0.2s; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                .animate-scale-up { animation: scaleUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes scaleUp { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
                .cursor-pointer { cursor: pointer; }
            `}</style>
        </div>
    );
};

export default ComplaintModal;